import {Box} from '@mui/material';
import React, {Fragment} from 'react';

import {AlertInfo, MainFooter, SentinelLoading, StyledGrid} from 'presentation/components';
import {translations} from 'presentation/translations-new';

import Tutorial from 'presentation/components/Tutorial';
import {AppointmentCardSkeleton} from 'presentation/modules/myAppointments/ui/components/AppointmentCardSkeleton';
import {Placement} from 'react-joyride';

import {AppointmentsHistoryCard} from './components/AppointmentsHistoryCard';
import {TypeAttendance} from 'presentation/modules/myAppointments/constants/typeAttendanceEnum';
import {AppointmentsHistoryFilterHeader} from './components/AppointmentsHistoryFilterHeader';
import {useMyAppointmentsListHistory} from './hooks/useMyAppointmentsListHistory';
import {AppointmentsHistoryEmptyState} from './components/AppointmentsHistoryEmptyState';
import {AppointmentsHistoryLoading} from './components/AppointmentsHistoryLoading';
import {StyledBox} from './styles';
import {HandleMessage} from 'presentation/components/HandleMessage';

const {
	telehealthDocumentsAccess: {historyAlert},
	tutorial: {step2, step3},
} = translations['pt-br'].privateFlow.myAppointments;

export function MyAppointmentsHistoryList() {
	const {
		isTablet,
		isMobile,
		appointmentsListHistoryError,
		appointmentsListHistoryLoading,
		appointmentsListHistoryFiltered,
		appointmentsListHistoryFilteredEmpty,
		handleGoToDetails,
		scrollableContentRef,
		loadingNewPageState,
		handleVisible,
		firstNonHealthIndex,
		hasTelemedicineOrEmergency,
		shouldHideScheduleButton,
		emptyStateDescription,
		handleNewAppointment,
		list,
		listHasCardWithFavoriteButton,
	} = useMyAppointmentsListHistory();

	if (appointmentsListHistoryLoading) {
		return <AppointmentsHistoryLoading />;
	}

	if (appointmentsListHistoryFilteredEmpty || appointmentsListHistoryError) {
		return (
			<AppointmentsHistoryEmptyState
				description={shouldHideScheduleButton ? '' : emptyStateDescription}
				hideButton={shouldHideScheduleButton}
				handleNewAppointment={handleNewAppointment}
			/>
		);
	}

	const seeHistoryDetails = {
		target: '#seeHistoryDetails',
		content: step2,
		placement: 'bottom' as Placement,
		disableBeacon: true,
	};
	const seeHistoryDetailsLikeButton = {
		target: '#seeHistoryDetailsLikeButton',
		content: <HandleMessage message={step3} />,
		placement: 'bottom' as Placement,
		disableBeacon: true,
	};

	const firstNonEmergencyIndex =
		list?.findIndex(item => item.typeAttendance !== TypeAttendance.EMERGENCY) ?? -1;
	const shouldShowStep3 = !listHasCardWithFavoriteButton && firstNonEmergencyIndex !== -1;
	const tutorialSteps = shouldShowStep3
		? [seeHistoryDetails, seeHistoryDetailsLikeButton]
		: [seeHistoryDetails];

	return (
		<Fragment>
			<Box p={isMobile ? 2 : 4} ref={scrollableContentRef}>
				<AlertInfo.Root sx={{mb: 2}}>
					<AlertInfo.Description>{historyAlert}</AlertInfo.Description>
				</AlertInfo.Root>
				<AppointmentsHistoryFilterHeader
					handleNewAppointment={handleNewAppointment}
					totalCount={appointmentsListHistoryFiltered.length}
				/>

				<StyledGrid.Root>
					{list?.map((appointment, index) => (
						<StyledGrid.Item key={index} sm={12} md={12} lg={6}>
							<AppointmentsHistoryCard
								appointment={appointment}
								handleGoToDetails={() => handleGoToDetails(appointment)}
								hasTutorialId={index === firstNonHealthIndex}
								hasTutorialLikeButtonId={index === firstNonEmergencyIndex}
							/>
						</StyledGrid.Item>
					))}

					{loadingNewPageState
						? Array.from({length: isTablet ? 1 : 2}).map((_, index) => (
								<StyledGrid.Item
									key={`skeleton${String(index)}`}
									sm={12}
									md={12}
									lg={6}>
									<AppointmentCardSkeleton />
								</StyledGrid.Item>
							))
						: null}

					<SentinelLoading
						sx={{
							visibility: 'hidden',
						}}
						onVisible={handleVisible}
						isLoading={loadingNewPageState}
					/>
				</StyledGrid.Root>
			</Box>

			<StyledBox>
				<MainFooter />
			</StyledBox>

			{hasTelemedicineOrEmergency ? (
				<Tutorial steps={tutorialSteps} storageKey="seeHistoryDetailsTutorial" />
			) : null}
		</Fragment>
	);
}
